import { isEmpty, flatten } from 'ramda';
import { joinArrayToString } from '../utils/string';
import {
  getDropdownListStops,
  isHighValueParcel,
  isPharmaParcel,
  isSmallsToteParcel,
} from '../features/routeDetail/model';
import {
  findStopByStopNumber,
  getManifestLocations,
  getServiceWindow,
} from './stop';
import { findLeadParcel } from './parcel';
import { routeParcels } from './route';

const ADDRESS_SEPARATOR = ', ';
const LABEL_SEPARATOR = ' - ';

export const formatStopAddress = (stop) =>
  joinArrayToString([
    stop.stopAddress?.contactName,
    stop.stopAddress?.address1,
  ]);

export const formatRouteStopLabel = (routeStop) => {
  const address = formatStopAddress(routeStop);

  return joinArrayToString(
    [routeStop.displayNumber, routeStop.stopAddress?.postcode, address],
    LABEL_SEPARATOR
  );
};

export const createStopOptions = (stops) =>
  stops.map((stop) => ({
    value: stop.stopNumber,
    label: formatRouteStopLabel(stop),
  }));

export const getMergeSplitTableData = (stops) =>
  stops.map((stop) => ({
    displayNumber: stop.displayNumber,
    sequenceNumber: stop.sequenceNumber,
    address: formatStopAddress(stop),
    postcode: stop.stopAddress?.postcode,
    totalParcels: stop.totalParcels,
    totalCadParcels: stop.totalCadParcels,
    eta: stop.eta || '',
    exception: stop.exception || '',
    window: getServiceWindow(stop),
    stopTime: stop.stopTime,
    highValue: isHighValueParcel(stop),
    pharma: isPharmaParcel(stop),
    smallsToteParcel: isSmallsToteParcel(stop),
    manifestLocation: getManifestLocations(stop),
  }));

export const prepareTableData = (parcel) =>
  Object.values(parcel).map(
    ({
      priority,
      parcelNumber,
      cadScanned,
      loaded,
      address,
      window,
      service,
      type,
      parcelCode,
      routeDate,
      parcelStatus,
      highValue,
      pharma,
      smallsToteParcel,
      manifestLocation,
    }) => ({
      priority,
      parcelNumber,
      cadScanned,
      loaded,
      address,
      window,
      service,
      type,
      parcelCode,
      routeDate,
      parcelStatus,
      highValue,
      pharma,
      smallsToteParcel,
      manifestLocation,
      key: parcelNumber,
    })
  );

export const getDropdownListSplitStop = (
  selectedParcels,
  availableStops,
  matchPostcodes
) => {
  if (isEmpty(selectedParcels)) {
    return [];
  }
  const leadParcel = findLeadParcel(Object.values(selectedParcels));
  const [postcode] = leadParcel.address.split(ADDRESS_SEPARATOR).slice(-1);

  return getDropdownListStops(
    { stopAddress: { postcode } },
    availableStops,
    matchPostcodes
  );
};

export const parcelsConsignmentValues = (parcels) => [
  ...new Set(
    parcels.reduce(
      (acc, parcel) =>
        parcel.consignmentNumber && (parcel.cadScanned || parcel.cadScan)
          ? [...acc, parcel.consignmentNumber + parcel.shipmentDate]
          : acc,
      []
    )
  ),
];

export const allowToSplitStop = (selectedParcels, remainingParcels) => {
  const selected = Array.isArray(selectedParcels)
    ? selectedParcels
    : Object.values(selectedParcels);
  const selectedConsignmentValues = parcelsConsignmentValues(selected);

  return !remainingParcels.some(
    ({ consignmentValue, cadScanned }) =>
      cadScanned && selectedConsignmentValues.includes(consignmentValue)
  );
};

export const parcelsWithSameConsignmentValues = (
  selectedParcels,
  remainingParcels
) => {
  const selected = Array.isArray(selectedParcels)
    ? selectedParcels
    : Object.values(selectedParcels);
  const selectedConsignmentValues = parcelsConsignmentValues(selected);
  return remainingParcels.filter(
    ({ consignmentValue, cadScanned, cadScan }) =>
      (cadScanned || cadScan) &&
      selectedConsignmentValues.includes(consignmentValue)
  );
};

export const getSelectedToteLabelNumbers = (parcels) => [
  ...new Set(
    parcels.reduce(
      (acc, { toteLabelNumber, smallsToteParcel }) =>
        smallsToteParcel && toteLabelNumber ? [...acc, toteLabelNumber] : acc,
      []
    )
  ),
];

export const isDisableToteTransfer = (selectedParcels, route) => {
  const parcels = Array.isArray(selectedParcels)
    ? selectedParcels
    : Object.values(selectedParcels);
  const selectedParcelCodes = parcels.map(({ parcelCode }) => parcelCode);
  const selectedToteLabelNumbers = getSelectedToteLabelNumbers(parcels);

  return routeParcels(route).some(
    ({ toteLabelNumber, parcelCode }) =>
      !selectedParcelCodes.includes(parcelCode) &&
      selectedToteLabelNumbers.includes(toteLabelNumber)
  );
};

export const includesToteLabelNumber =
  (selectedToteLabelNumbers, selectedParcelCodes) =>
  ({ toteLabelNumber, parcelCode }) =>
    selectedToteLabelNumbers.includes(toteLabelNumber) ||
    selectedParcelCodes.includes(parcelCode);

export const getAffectedToteStops = (selectedStop, stops) => {
  const selectedParcels = flatten(selectedStop.map(({ parcel }) => parcel));
  const selectedToteLabelNumbers = getSelectedToteLabelNumbers(selectedParcels);
  const selectedParcelCodes = selectedParcels.map(
    ({ parcelCode }) => parcelCode
  );
  const condition = includesToteLabelNumber(
    selectedToteLabelNumbers,
    selectedParcelCodes
  );
  return stops.reduce((affected, stop) => {
    const { parcel = [] } = stop;
    const processedParcels = parcel.filter(condition);
    return processedParcels.length
      ? [...affected, { ...stop, parcel: processedParcels }]
      : affected;
  }, []);
};

export const getAffectedConsignmentStops = (selectedStop, stops) =>
  selectedStop.map((stop) => {
    const selectedConsignmentValues = parcelsConsignmentValues(stop.parcel);
    const selectedParcelNumbers = stop.parcel.map(
      ({ parcelNumber }) => parcelNumber
    );
    const processedParcels = findStopByStopNumber(
      stops,
      stop.stopNumber
    )?.parcel.filter(
      (item) =>
        (item.consignmentNumber &&
          item.cadScanned &&
          selectedConsignmentValues.includes(
            item.consignmentNumber + item.shipmentDate
          )) ||
        selectedParcelNumbers.includes(item.parcelNumber)
    );
    return processedParcels.length
      ? { ...stop, parcel: processedParcels }
      : stop;
  });

const parcelsWasAdded = (prevStops, nextStops) => {
  const prevParcels = flatten(prevStops.map(({ parcel }) => parcel));
  const nextParcels = flatten(nextStops.map(({ parcel }) => parcel));
  return nextParcels.length > prevParcels.length;
};

export const getAffectedStops = (selectedStop, stops, conditions) => {
  for (let i = 0; i < conditions.length; i++) {
    const conditionFunction = conditions[i];
    const processedStops = conditionFunction(selectedStop, stops);
    if (parcelsWasAdded(selectedStop, processedStops)) {
      return getAffectedStops(processedStops, stops, conditions);
    }
  }
  return selectedStop;
};

export const hasRouteUnprocessedRequest = (unprocessedRequests, stops) => {
  const stopParcels = flatten(stops.map(({ parcel }) => parcel));
  const requestParcelCodes = unprocessedRequests.map(
    ({ parcelCode }) => parcelCode
  );
  return stopParcels.some(({ parcelCode }) =>
    requestParcelCodes.includes(parcelCode)
  );
};
